import { Injectable, NgZone } from '@angular/core';
import { BrowserDiagnosticsService } from './browser-diagnostics.service';
import { DiagnosticEntry, DiagnosticSummaryItem, DiagnosticSupportDataThreshold } from './diagnostic-types';

const REFRESH_INTERVAL_MS = 1000;
const DEFER_RENDER_MS = 100;
const RECENT_ENTRIES_LIMIT = 10;
const SLOW_ENTRY_WARNING_MS = 500;
const SLOW_ENTRY_DANGER_MS = 1500;
const BYTES_PER_MB = 1024 * 1024;
const MAX_METADATA_LENGTH = 120;
const SUPPORT_DATA_GROUP = 'support-data';
const CACHE_STATUS_FRESH = 'fresh';


interface SupportDataDiagnosticAccumulator {
    readonly formName: string;
    readonly sourceKey: string;
    readonly phase: string;
    calls: number;
    errors: number;
    cacheHits: number;
    staleHits: number;
    totalMs: number;
    maxMs: number;
    networkCalls: number;
    totalNetworkMs: number;
    cacheCalls: number;
    totalCacheMs: number;
    latestStatus: string;
    readonly triggers: Set<string>;
}

interface SupportDataDiagnosticSummaryItem {
    readonly formName: string;
    readonly sourceKey: string;
    readonly phase: string;
    readonly calls: number;
    readonly errors: number;
    readonly cacheHits: number;
    readonly staleHits: number;
    readonly averageMs: number;
    readonly maxMs: number;
    readonly averageNetworkMs: number | null;
    readonly averageCacheMs: number | null;
    readonly latestStatus: string;
    readonly triggers: string;
    readonly thresholdBreaches: readonly string[];
    readonly bottleneck: boolean;
}

@Injectable({ providedIn: 'root' })
export class DiagnosticsOverlayService {
    private readonly overlayId = 'fitogest-diagnostics-overlay';

    private initialized = false;

    private expanded = false;

    private rootElement: HTMLDivElement | null = null;

    private refreshTimerId: number | null = null;

    public constructor(
        private readonly diagnostics: BrowserDiagnosticsService,
        private readonly ngZone: NgZone,
    ) {}

    public initialize(): void {
        if (this.initialized || !this.diagnostics.isEnabled() || typeof document === 'undefined') {
            return;
        }

        if (!document.body) {
            window.setTimeout(() => this.initialize(), DEFER_RENDER_MS);
            return;
        }

        this.initialized = true;

        this.ngZone.runOutsideAngular(() => {
            this.createOverlay();
            this.render();

            this.refreshTimerId = window.setInterval(() => this.render(), REFRESH_INTERVAL_MS);
            window.addEventListener('beforeunload', () => this.destroy(), { once: true });
        });
    }

    public destroy(): void {
        if (this.refreshTimerId !== null) {
            window.clearInterval(this.refreshTimerId);
            this.refreshTimerId = null;
        }

        this.rootElement?.remove();
        this.rootElement = null;
        this.initialized = false;
    }

    private createOverlay(): void {
        const existingOverlay = document.getElementById(this.overlayId);

        if (existingOverlay) {
            existingOverlay.remove();
        }

        const rootElement = document.createElement('div');
        rootElement.id = this.overlayId;
        rootElement.style.position = 'fixed';
        rootElement.style.right = '16px';
        rootElement.style.bottom = '16px';
        rootElement.style.zIndex = '2147483647';
        rootElement.style.fontFamily = 'Arial, Helvetica, sans-serif';
        rootElement.style.color = '#172033';
        rootElement.style.pointerEvents = 'auto';
        rootElement.addEventListener('click', (event: MouseEvent) => this.handleClick(event));

        document.body.appendChild(rootElement);
        this.rootElement = rootElement;
    }

    private handleClick(event: MouseEvent): void {
        const target = event.target;

        if (!(target instanceof HTMLElement)) {
            return;
        }

        const action = target.dataset['diagnosticsAction'];

        if (!action) {
            return;
        }

        if (action === 'toggle') {
            this.expanded = !this.expanded;
            this.render();
            return;
        }

        if (action === 'refresh') {
            this.render();
            return;
        }

        if (action === 'clear') {
            this.diagnostics.clear();
            this.render();
            return;
        }

        if (action === 'copy') {
            this.copyEntries().catch((error: unknown) => console.error('[Fitogest diagnostics] Could not copy entries.', error));
        }
    }

    private render(): void {
        if (!this.rootElement) {
            return;
        }

        const entries = this.diagnostics.getEntries();
        const summary = this.diagnostics.summary();
        const totalDurationMs = entries.reduce((total, entry) => total + entry.durationMs, 0);
        const errors = entries.filter((entry) => !!entry.error).length;
        const slowestEntry = this.getSlowestEntry(entries);
        const latestEntry = entries.at(-1) ?? null;

        this.rootElement.innerHTML = this.expanded
            ? this.renderExpanded(entries, summary, totalDurationMs, errors, slowestEntry, latestEntry)
            : this.renderCollapsed(entries, errors, slowestEntry);
    }

    private renderCollapsed(entries: DiagnosticEntry[], errors: number, slowestEntry: DiagnosticEntry | null): string {
        const slowestText = slowestEntry ? `${Math.round(slowestEntry.durationMs)} ms` : '0 ms';
        const statusColor = errors > 0 ? '#b42318' : '#14532d';

        return `
            <button type="button" data-diagnostics-action="toggle" style="${this.buttonStyle(statusColor)}">
                <span style="font-weight: 700; pointer-events: none;">Diagnostics</span>
                <span style="${this.badgeStyle()}; pointer-events: none;">${entries.length}</span>
                <span style="opacity: .85; pointer-events: none;">slowest ${this.escapeHtml(slowestText)}</span>
            </button>
        `;
    }

    private renderExpanded(
        entries: DiagnosticEntry[],
        summary: DiagnosticSummaryItem[],
        totalDurationMs: number,
        errors: number,
        slowestEntry: DiagnosticEntry | null,
        latestEntry: DiagnosticEntry | null,
    ): string {
        const memoryText = latestEntry?.memoryEnd?.usedJsHeapSize
            ? `${this.formatMb(latestEntry.memoryEnd.usedJsHeapSize)} MB JS heap`
            : 'Memory unavailable';

        return `
            <section style="${this.panelStyle()}">
                <header style="${this.headerStyle()}">
                    <div>
                        <div style="font-size: 14px; font-weight: 700;">Fitogest Diagnostics</div>
                        <div style="font-size: 11px; opacity: .72;">Debug overlay · no production</div>
                    </div>
                    <button type="button" data-diagnostics-action="toggle" style="${this.iconButtonStyle()}" title="Minimize">×</button>
                </header>

                <div style="${this.statsGridStyle()}">
                    ${this.renderStat('Entries', String(entries.length))}
                    ${this.renderStat('Total time', `${Math.round(totalDurationMs)} ms`)}
                    ${this.renderStat('Errors', String(errors))}
                    ${this.renderStat('Memory', memoryText)}
                </div>

                <div style="${this.slowestStyle(slowestEntry)}">
                    ${this.renderSlowest(slowestEntry)}
                </div>

                <div style="display: flex; gap: 8px; margin: 10px 0; flex-wrap: wrap;">
                    <button type="button" data-diagnostics-action="refresh" style="${this.actionButtonStyle()}">Refresh</button>
                    <button type="button" data-diagnostics-action="copy" style="${this.actionButtonStyle()}">Copy JSON</button>
                    <button
                        type="button"
                        data-diagnostics-action="clear"
                        style="${this.actionButtonStyle('#fff7ed', '#9a3412')}"
                    >Clear</button>
                </div>

                <div style="${this.sectionTitleStyle()}">Support data diagnostics</div>
                ${this.renderSupportDataDiagnostics(entries)}

                <div style="${this.sectionTitleStyle()}">Summary by process</div>
                ${this.renderSummaryTable(summary)}

                <div style="${this.sectionTitleStyle()}">Latest entries</div>
                ${this.renderRecentEntries(entries)}
            </section>
        `;
    }

    private renderSupportDataDiagnostics(entries: DiagnosticEntry[]): string {
        const summary = this.buildSupportDataSummary(entries);

        if (summary.length === 0) {
            return this.renderEmptyState('No support-data metrics yet. Open Parcelas or any form using ReactiveFormLoadSession metrics.');
        }

        const rows = summary.map((item) => `
            <tr>
                <td style="${this.cellStyle()}">${this.escapeHtml(item.formName)}</td>
                <td style="${this.cellStyle(this.supportDataSourceColor(item))}">${this.escapeHtml(item.sourceKey)}</td>
                <td style="${this.cellStyle()}">${this.escapeHtml(item.phase)}</td>
                <td style="${this.numericCellStyle()}">${item.calls}</td>
                <td style="${this.numericCellStyle()}">${item.cacheHits}</td>
                <td style="${this.numericCellStyle(item.staleHits > 0 ? '#9a3412' : undefined)}">${item.staleHits}</td>
                <td style="${this.numericCellStyle(this.durationColor(item.averageMs))}">${Math.round(item.averageMs)}</td>
                <td style="${this.numericCellStyle(this.durationColor(item.maxMs))}">${Math.round(item.maxMs)}</td>
                <td style="${this.numericCellStyle(this.durationColor(item.averageNetworkMs ?? 0))}">
                    ${item.averageNetworkMs === null ? '-' : Math.round(item.averageNetworkMs)}
                </td>
                <td style="${this.numericCellStyle(this.durationColor(item.averageCacheMs ?? 0))}">
                    ${item.averageCacheMs === null ? '-' : Math.round(item.averageCacheMs)}
                </td>
                <td style="${this.cellStyle(item.errors > 0 ? '#b42318' : undefined)}">
                    ${this.escapeHtml(item.latestStatus)} · ${this.escapeHtml(item.triggers)}
                </td>
                <td style="${this.cellStyle(this.supportDataAlertColor(item))}">
                    ${this.escapeHtml(this.formatSupportDataAlerts(item))}
                </td>
            </tr>
        `).join('');

        return `
            <div style="${this.tableWrapperStyle()}">
                <table style="${this.tableStyle('1080px')}">
                    <thead>
                        <tr>
                            <th style="${this.headerCellStyle()}">Form</th>
                            <th style="${this.headerCellStyle()}">Source</th>
                            <th style="${this.headerCellStyle()}">Phase</th>
                            <th style="${this.headerCellStyle('right')}">Calls</th>
                            <th style="${this.headerCellStyle('right')}">Cache</th>
                            <th style="${this.headerCellStyle('right')}">Stale</th>
                            <th style="${this.headerCellStyle('right')}">Avg</th>
                            <th style="${this.headerCellStyle('right')}">Max</th>
                            <th style="${this.headerCellStyle('right')}">Net avg</th>
                            <th style="${this.headerCellStyle('right')}">Cache avg</th>
                            <th style="${this.headerCellStyle()}">Status</th>
                            <th style="${this.headerCellStyle()}">Alert</th>
                        </tr>
                    </thead>
                    <tbody>${rows}</tbody>
                </table>
            </div>
        `;
    }

    private buildSupportDataSummary(entries: DiagnosticEntry[]): SupportDataDiagnosticSummaryItem[] {
        const grouped = new Map<string, SupportDataDiagnosticAccumulator>();

        for (const entry of entries) {
            if (entry.group !== SUPPORT_DATA_GROUP) {
                continue;
            }

            const metadata = entry.metadata ?? {};
            const formName = this.metadataText(metadata, 'formName', 'unknown');
            const sourceKey = this.metadataText(metadata, 'sourceKey', entry.name);
            const phase = this.metadataText(metadata, 'phase', 'all');
            const cacheStatus = this.metadataText(metadata, 'cacheStatus', 'unknown');
            const trigger = this.metadataText(metadata, 'trigger', 'initial');
            const key = `${formName}::${sourceKey}::${phase}`;
            const current = grouped.get(key) ?? this.createSupportDataAccumulator(formName, sourceKey, phase);
            const networkMs = this.findMetricValue(entry, 'networkMs');

            current.calls += 1;
            current.errors += entry.error ? 1 : 0;
            current.cacheHits += this.metadataBoolean(metadata, 'cacheHit') ? 1 : 0;
            current.staleHits += this.metadataBoolean(metadata, 'staleHit') ? 1 : 0;
            current.totalMs += entry.durationMs;
            current.maxMs = Math.max(current.maxMs, entry.durationMs);

            if (cacheStatus === CACHE_STATUS_FRESH) {
                current.cacheCalls += 1;
                current.totalCacheMs += entry.durationMs;
            }
            current.latestStatus = entry.error ? 'error' : cacheStatus;
            current.triggers.add(trigger);

            if (networkMs !== null) {
                current.networkCalls += 1;
                current.totalNetworkMs += networkMs;
            }

            grouped.set(key, current);
        }

        return [...grouped.values()]
            .map((item) => this.toSupportDataSummaryItem(item))
            .sort((a, b) => b.maxMs - a.maxMs);
    }

    private createSupportDataAccumulator(
        formName: string,
        sourceKey: string,
        phase: string,
    ): SupportDataDiagnosticAccumulator {
        return {
            formName,
            sourceKey,
            phase,
            calls: 0,
            errors: 0,
            cacheHits: 0,
            staleHits: 0,
            totalMs: 0,
            maxMs: 0,
            networkCalls: 0,
            totalNetworkMs: 0,
            cacheCalls: 0,
            totalCacheMs: 0,
            latestStatus: 'unknown',
            triggers: new Set<string>(),
        };
    }

    private toSupportDataSummaryItem(item: SupportDataDiagnosticAccumulator): SupportDataDiagnosticSummaryItem {
        const averageMs = item.calls > 0 ? item.totalMs / item.calls : 0;
        const averageNetworkMs = item.networkCalls > 0 ? item.totalNetworkMs / item.networkCalls : null;
        const averageCacheMs = item.cacheCalls > 0 ? item.totalCacheMs / item.cacheCalls : null;
        const thresholdBreaches = this.findSupportDataThresholdBreaches(
            item.formName,
            item.sourceKey,
            item.phase,
            averageMs,
            item.maxMs,
            averageNetworkMs,
            averageCacheMs,
        );
        const networkBottleneck = averageNetworkMs !== null && averageNetworkMs >= SLOW_ENTRY_WARNING_MS;
        const bottleneck = thresholdBreaches.length > 0 || item.maxMs >= SLOW_ENTRY_WARNING_MS || networkBottleneck || item.errors > 0;

        return {
            formName: item.formName,
            sourceKey: item.sourceKey,
            phase: item.phase,
            calls: item.calls,
            errors: item.errors,
            cacheHits: item.cacheHits,
            staleHits: item.staleHits,
            averageMs,
            maxMs: item.maxMs,
            averageNetworkMs,
            averageCacheMs,
            latestStatus: item.latestStatus,
            triggers: [...item.triggers].join(', '),
            thresholdBreaches,
            bottleneck,
        };
    }

    private findSupportDataThresholdBreaches(
        formName: string,
        sourceKey: string,
        phase: string,
        averageMs: number,
        maxMs: number,
        averageNetworkMs: number | null,
        averageCacheMs: number | null,
    ): string[] {
        const breaches = new Set<string>();

        for (const threshold of this.diagnostics.getSupportDataThresholds()) {
            if (!this.supportDataThresholdMatches(threshold, formName, sourceKey, phase)) {
                continue;
            }

            if (threshold.maxAverageMs !== undefined && averageMs >= threshold.maxAverageMs) {
                breaches.add(`avg ≥ ${Math.round(threshold.maxAverageMs)} ms`);
            }

            if (threshold.maxMs !== undefined && maxMs >= threshold.maxMs) {
                breaches.add(`max ≥ ${Math.round(threshold.maxMs)} ms`);
            }

            if (
                threshold.maxAverageNetworkMs !== undefined
                && averageNetworkMs !== null
                && averageNetworkMs >= threshold.maxAverageNetworkMs
            ) {
                breaches.add(`net ≥ ${Math.round(threshold.maxAverageNetworkMs)} ms`);
            }

            if (threshold.maxCacheMs !== undefined && averageCacheMs !== null && averageCacheMs >= threshold.maxCacheMs) {
                breaches.add(`cache ≥ ${Math.round(threshold.maxCacheMs)} ms`);
            }
        }

        return [...breaches];
    }

    private supportDataThresholdMatches(
        threshold: DiagnosticSupportDataThreshold,
        formName: string,
        sourceKey: string,
        phase: string,
    ): boolean {
        return (threshold.formName === undefined || threshold.formName === formName)
            && (threshold.sourceKey === undefined || threshold.sourceKey === sourceKey)
            && (threshold.phase === undefined || threshold.phase === phase);
    }

    private supportDataAlertColor(item: SupportDataDiagnosticSummaryItem): string | undefined {
        if (item.errors > 0) {
            return '#b42318';
        }

        if (item.thresholdBreaches.length > 0) {
            return '#9a3412';
        }

        return undefined;
    }

    private formatSupportDataAlerts(item: SupportDataDiagnosticSummaryItem): string {
        if (item.errors > 0) {
            return `Errors: ${item.errors}`;
        }

        if (item.thresholdBreaches.length > 0) {
            return item.thresholdBreaches.join(', ');
        }

        return 'OK';
    }

    private supportDataSourceColor(item: SupportDataDiagnosticSummaryItem): string | undefined {
        if (item.errors > 0) {
            return '#b42318';
        }

        if (item.bottleneck) {
            return '#9a3412';
        }

        return undefined;
    }

    private renderSummaryTable(summary: DiagnosticSummaryItem[]): string {
        if (summary.length === 0) {
            return this.renderEmptyState('No diagnostic entries yet. Open a form or reload the current view.');
        }

        const rows = summary.map((item) => `
            <tr>
                <td style="${this.cellStyle()}">${this.escapeHtml(item.group)}</td>
                <td style="${this.cellStyle()}">${this.escapeHtml(item.name)}</td>
                <td style="${this.numericCellStyle()}">${item.calls}</td>
                <td style="${this.numericCellStyle()}">${Math.round(item.totalMs)}</td>
                <td style="${this.numericCellStyle()}">${Math.round(item.averageMs)}</td>
                <td style="${this.numericCellStyle()}">${Math.round(item.maxMs)}</td>
                <td style="${this.numericCellStyle(item.errors > 0 ? '#b42318' : undefined)}">${item.errors}</td>
            </tr>
        `).join('');

        return `
            <div style="${this.tableWrapperStyle()}">
                <table style="${this.tableStyle()}">
                    <thead>
                        <tr>
                            <th style="${this.headerCellStyle()}">Group</th>
                            <th style="${this.headerCellStyle()}">Process</th>
                            <th style="${this.headerCellStyle('right')}">Calls</th>
                            <th style="${this.headerCellStyle('right')}">Total</th>
                            <th style="${this.headerCellStyle('right')}">Avg</th>
                            <th style="${this.headerCellStyle('right')}">Max</th>
                            <th style="${this.headerCellStyle('right')}">Errors</th>
                        </tr>
                    </thead>
                    <tbody>${rows}</tbody>
                </table>
            </div>
        `;
    }

    private renderRecentEntries(entries: DiagnosticEntry[]): string {
        if (entries.length === 0) {
            return this.renderEmptyState('No recent entries.');
        }

        const recentEntries = entries.slice(-RECENT_ENTRIES_LIMIT).reverse();
        const rows = recentEntries.map((entry) => `
            <tr>
                <td style="${this.cellStyle()}">${entry.id}</td>
                <td style="${this.cellStyle()}">${this.escapeHtml(entry.group)}</td>
                <td style="${this.cellStyle()}">
                    <div>${this.escapeHtml(entry.name)}</div>
                    <div style="opacity: .65; font-size: 10px;">${this.escapeHtml(this.formatMetadata(entry.metadata))}</div>
                </td>
                <td style="${this.numericCellStyle(this.durationColor(entry.durationMs))}">${Math.round(entry.durationMs)}</td>
                <td style="${this.cellStyle(entry.error ? '#b42318' : undefined)}">${this.escapeHtml(entry.error ?? '')}</td>
            </tr>
        `).join('');

        return `
            <div style="${this.tableWrapperStyle()}">
                <table style="${this.tableStyle()}">
                    <thead>
                        <tr>
                            <th style="${this.headerCellStyle()}">Id</th>
                            <th style="${this.headerCellStyle()}">Group</th>
                            <th style="${this.headerCellStyle()}">Process</th>
                            <th style="${this.headerCellStyle('right')}">Ms</th>
                            <th style="${this.headerCellStyle()}">Error</th>
                        </tr>
                    </thead>
                    <tbody>${rows}</tbody>
                </table>
            </div>
        `;
    }

    private renderStat(label: string, value: string): string {
        return `
            <div style="${this.statCardStyle()}">
                <div style="font-size: 10px; opacity: .68; text-transform: uppercase; letter-spacing: .04em;">
                    ${this.escapeHtml(label)}
                </div>
                <div style="font-size: 13px; font-weight: 700; margin-top: 3px;">${this.escapeHtml(value)}</div>
            </div>
        `;
    }

    private renderSlowest(entry: DiagnosticEntry | null): string {
        if (!entry) {
            return 'Slowest process: none yet.';
        }

        return [
            'Slowest process: ',
            `<strong>${this.escapeHtml(entry.group)} / ${this.escapeHtml(entry.name)}</strong>`,
            ` · ${Math.round(entry.durationMs)} ms`,
        ].join('');
    }

    private renderEmptyState(text: string): string {
        const style = 'padding: 12px; border: 1px dashed #cbd5e1; border-radius: 10px; font-size: 12px; color: #64748b;';

        return `<div style="${style}">${this.escapeHtml(text)}</div>`;
    }

    private getSlowestEntry(entries: DiagnosticEntry[]): DiagnosticEntry | null {
        return entries.reduce<DiagnosticEntry | null>((slowest, entry) => {
            if (!slowest || entry.durationMs > slowest.durationMs) {
                return entry;
            }

            return slowest;
        }, null);
    }

    private async copyEntries(): Promise<void> {
        const json = JSON.stringify(this.diagnostics.getEntries(), null, 2);

        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(json);
            return;
        }

        const textArea = document.createElement('textarea');
        textArea.value = json;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
    }

    private metadataText(metadata: Record<string, unknown>, key: string, fallback: string): string {
        const value = metadata[key];

        return typeof value === 'string' && value.trim() !== '' ? value : fallback;
    }

    private metadataBoolean(metadata: Record<string, unknown>, key: string): boolean {
        return metadata[key] === true;
    }

    private findMetricValue(entry: DiagnosticEntry, metricName: string): number | null {
        const metric = entry.metrics.find((item) => item.name === metricName);

        return metric && Number.isFinite(metric.value) ? metric.value : null;
    }

    private formatMetadata(metadata?: Record<string, unknown>): string {
        if (!metadata || Object.keys(metadata).length === 0) {
            return '';
        }

        const formatted = JSON.stringify(metadata);

        if (formatted.length <= MAX_METADATA_LENGTH) {
            return formatted;
        }

        return `${formatted.slice(0, MAX_METADATA_LENGTH)}...`;
    }

    private formatMb(value: number): string {
        return (value / BYTES_PER_MB).toFixed(1);
    }

    private escapeHtml(value: string): string {
        return value
            .replaceAll('&', '&amp;')
            .replaceAll('<', '&lt;')
            .replaceAll('>', '&gt;')
            .replaceAll('"', '&quot;')
            .replaceAll("'", '&#039;');
    }

    private durationColor(durationMs: number): string | undefined {
        if (durationMs >= SLOW_ENTRY_DANGER_MS) {
            return '#b42318';
        }

        if (durationMs >= SLOW_ENTRY_WARNING_MS) {
            return '#9a3412';
        }

        return undefined;
    }

    private buttonStyle(backgroundColor: string): string {
        return [
            'display: flex',
            'align-items: center',
            'gap: 8px',
            'border: 0',
            'border-radius: 999px',
            'padding: 10px 14px',
            `background: ${backgroundColor}`,
            'color: #ffffff',
            'box-shadow: 0 12px 34px rgba(15, 23, 42, .28)',
            'cursor: pointer',
            'font-size: 12px',
        ].join('; ');
    }

    private badgeStyle(): string {
        return [
            'display: inline-flex',
            'min-width: 22px',
            'height: 22px',
            'align-items: center',
            'justify-content: center',
            'border-radius: 999px',
            'background: rgba(255, 255, 255, .22)',
            'font-weight: 700',
        ].join('; ');
    }

    private panelStyle(): string {
        return [
            'width: min(920px, calc(100vw - 32px))',
            'max-height: min(760px, calc(100vh - 32px))',
            'overflow: auto',
            'background: rgba(255, 255, 255, .98)',
            'border: 1px solid #dbe3ef',
            'border-radius: 16px',
            'box-shadow: 0 24px 80px rgba(15, 23, 42, .30)',
            'padding: 14px',
        ].join('; ');
    }

    private headerStyle(): string {
        return [
            'display: flex',
            'justify-content: space-between',
            'align-items: center',
            'border-bottom: 1px solid #e2e8f0',
            'padding-bottom: 10px',
            'margin-bottom: 10px',
        ].join('; ');
    }

    private iconButtonStyle(): string {
        return [
            'width: 30px',
            'height: 30px',
            'border: 0',
            'border-radius: 999px',
            'background: #eff6ff',
            'color: #1d4ed8',
            'cursor: pointer',
            'font-size: 20px',
            'line-height: 20px',
        ].join('; ');
    }

    private statsGridStyle(): string {
        return [
            'display: grid',
            'grid-template-columns: repeat(4, minmax(0, 1fr))',
            'gap: 8px',
            'margin-bottom: 10px',
        ].join('; ');
    }

    private statCardStyle(): string {
        return [
            'border: 1px solid #e2e8f0',
            'border-radius: 12px',
            'padding: 9px',
            'background: #f8fafc',
            'min-width: 0',
        ].join('; ');
    }

    private slowestStyle(entry: DiagnosticEntry | null): string {
        const color = entry ? this.durationColor(entry.durationMs) : undefined;
        const background = color === '#b42318' ? '#fef2f2' : color === '#9a3412' ? '#fff7ed' : '#f0fdf4';

        return [
            'border-radius: 12px',
            'padding: 10px',
            `background: ${background}`,
            `color: ${color ?? '#166534'}`,
            'font-size: 12px',
        ].join('; ');
    }

    private actionButtonStyle(backgroundColor: string = '#eff6ff', color: string = '#1d4ed8'): string {
        return [
            'border: 0',
            'border-radius: 9px',
            'padding: 8px 10px',
            `background: ${backgroundColor}`,
            `color: ${color}`,
            'font-size: 12px',
            'font-weight: 700',
            'cursor: pointer',
        ].join('; ');
    }

    private sectionTitleStyle(): string {
        return 'font-size: 12px; font-weight: 700; margin: 12px 0 6px; color: #334155;';
    }

    private tableWrapperStyle(): string {
        return 'overflow: auto; border: 1px solid #e2e8f0; border-radius: 12px; max-height: 260px;';
    }

    private tableStyle(minWidth: string = '720px'): string {
        return `width: 100%; border-collapse: collapse; font-size: 11px; min-width: ${minWidth};`;
    }

    private headerCellStyle(textAlign: 'left' | 'right' = 'left'): string {
        return [
            'position: sticky',
            'top: 0',
            'background: #f8fafc',
            'border-bottom: 1px solid #e2e8f0',
            'padding: 8px',
            `text-align: ${textAlign}`,
            'font-weight: 700',
            'color: #475569',
        ].join('; ');
    }

    private cellStyle(color?: string): string {
        return [
            'border-bottom: 1px solid #eef2f7',
            'padding: 7px 8px',
            'vertical-align: top',
            `color: ${color ?? '#172033'}`,
        ].join('; ');
    }

    private numericCellStyle(color?: string): string {
        return `${this.cellStyle(color)}; text-align: right; font-variant-numeric: tabular-nums;`;
    }
}
